/**
 * Response envelope for `GET /api/word/{word}` (issue #353).
 *
 * Must stay in sync with `WordDefinitionsResponse` / `ParentEntry` in
 * `server/crates/lexicontest-server/src/handlers/definitions.rs`.
 *
 * `records` are the extractor's raw JSON lines (a lemma or reference record). The
 * full lemma/reference record schema is owned by the dataset (#332) and the
 * definition-modal front-end issue; here they are typed loosely as `unknown` so
 * this envelope type does not couple to the record internals. An empty `records`
 * array is a coverage gap (the word — or a followed parent — has no record).
 */
export interface ParentEntry {
  readonly word: string;
  readonly records: readonly unknown[];
}

export interface WordDefinitions {
  readonly word: string;
  readonly records: readonly unknown[];
  readonly parents: readonly ParentEntry[];
}

function isParentEntry(value: unknown): value is ParentEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    'word' in value &&
    'records' in value &&
    typeof (value as ParentEntry).word === 'string' &&
    Array.isArray((value as ParentEntry).records)
  );
}

export function isWordDefinitions(value: unknown): value is WordDefinitions {
  return (
    typeof value === 'object' &&
    value !== null &&
    'word' in value &&
    'records' in value &&
    'parents' in value &&
    typeof (value as WordDefinitions).word === 'string' &&
    Array.isArray((value as WordDefinitions).records) &&
    Array.isArray((value as WordDefinitions).parents) &&
    (value as WordDefinitions).parents.every(isParentEntry)
  );
}

/**
 * Record-detail types for rendering `records[]` in the definition modal (#354).
 *
 * The record schema is owned by the extractor/dataset (#332) — see
 * `tools/extract_definitions.py`. It is intentionally NOT part of the server
 * response type (the server passes each record through as opaque JSON), so these
 * shapes live on the client only. The two record variants are discriminated by
 * the presence of `pos_blocks`: a LEMMA record always has it; a REFERENCE record
 * never does and instead carries a top-level `form_of` list.
 *
 * Guards check only the discriminator + top-level shape; nested fields are
 * declared for typing but rendered defensively (the source is external, so a
 * missing/oddly-shaped optional field should degrade gracefully rather than drop
 * the whole record).
 */

/** A single dictionary sense within a part-of-speech block. */
export interface Sense {
  readonly definition: string;
  /** Usage examples (extractor caps at <=2). Omitted when none. */
  readonly examples?: readonly string[];
  /** Grammar/usage tags (e.g. "countable"). Omitted when none. */
  readonly tags?: readonly string[];
  /** Parent headwords this inflected sense is a form of. Plain strings. */
  readonly form_of?: readonly string[];
  /** Parent headwords this sense is an alternative of. Plain strings. */
  readonly alt_of?: readonly string[];
}

/** A part-of-speech block grouping senses (e.g. all "noun" senses). */
export interface PosBlock {
  readonly pos: string;
  readonly senses: readonly Sense[];
}

/** An inflected form of the headword (e.g. plural, past tense). */
export interface WordForm {
  readonly form: string;
  readonly tags?: readonly string[];
}

/** A lemma record: the headword's own dictionary entry. */
export interface LemmaRecord {
  readonly word: string;
  /** Etymology number for homographs; absent/null for single-etymology words. */
  readonly etym?: number | null;
  readonly etymology_text?: string;
  readonly ipa?: readonly string[];
  readonly forms?: readonly WordForm[];
  readonly pos_blocks: readonly PosBlock[];
}

/** A top-level cross-reference in a reference record. */
export interface FormOfRef {
  readonly parent: string;
  readonly label: string;
  readonly kind?: string;
}

/** A reference record: an inflection-only word pointing at parent lemmas. */
export interface ReferenceRecord {
  readonly word: string;
  readonly form_of: readonly FormOfRef[];
}

/** True when `record` is a lemma record (has `pos_blocks`). */
export function isLemmaRecord(record: unknown): record is LemmaRecord {
  return (
    typeof record === 'object' &&
    record !== null &&
    'pos_blocks' in record &&
    Array.isArray((record as LemmaRecord).pos_blocks)
  );
}

/**
 * True when `record` is a reference record: a top-level `form_of` array and no
 * `pos_blocks`. The `pos_blocks` exclusion matters because lemma senses also
 * carry `form_of`, but only at the sense level — never top level.
 */
export function isReferenceRecord(record: unknown): record is ReferenceRecord {
  return (
    typeof record === 'object' &&
    record !== null &&
    !('pos_blocks' in record) &&
    'form_of' in record &&
    Array.isArray((record as ReferenceRecord).form_of)
  );
}
